import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, Loader2, User, Mail, CalendarClock, RotateCcw, XCircle, Sparkles, FileText, Trash2 } from "lucide-react";
import AppShell from "../components/AppShell";
import { startCheckout } from "../components/UpgradeDialog";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail, fmtDateFull } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function Account() {
    const { user, refreshUser } = useAuth();
    const [sub, setSub] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/account/subscription").then((r) => setSub(r.data)).catch(() => toast.error("Could not load subscription"));
    useEffect(() => { load(); }, []);

    const act = async (path, okMsg) => {
        setBusy(true);
        try { await api.post(path); toast.success(okMsg); await Promise.all([load(), refreshUser()]); }
        catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong"); }
        finally { setBusy(false); }
    };

    const wipe = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/statements");
            await Promise.all(data.map((s) => api.delete(`/statements/${s.id}`)));
            toast.success("All statements and transactions deleted"); load();
        } catch (e) { toast.error("Could not delete data"); } finally { setBusy(false); }
    };

    if (!sub) return <AppShell title="Account" subtitle="You & your plan" testid="account-page"><div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></AppShell>;
    const pro = sub.plan_status === "pro";
    const isAdmin = sub.role === "admin";

    return (
        <AppShell title="Your account & plan" subtitle="Account" testid="account-page">
            <section className="rounded-2xl border border-border bg-card p-6" data-testid="account-profile">
                <div className="grid gap-4 sm:grid-cols-3">
                    <Info icon={User} label="Name" value={user?.name} testid="account-name" />
                    <Info icon={Mail} label="Email" value={user?.email} testid="account-email" />
                    <Info icon={FileText} label="Data" value={`${sub.stats.transactions} transactions · ${sub.stats.statements} statements`} testid="account-data" />
                </div>
            </section>

            <section className={`mt-6 rounded-2xl border p-6 ${pro ? "border-accent/30 bg-accent/5" : "border-border bg-card"}`} data-testid="account-plan-card">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <Crown className={`h-5 w-5 ${pro ? "text-accent" : "text-muted-foreground"}`} />
                            <h2 className="font-heading text-lg font-bold text-foreground" data-testid="account-plan-name">{pro ? "Sift Pro" : "Free plan"}</h2>
                            {pro && sub.cancel_at_period_end ? <Badge className="border border-accent/30 bg-accent/15 text-accent" data-testid="plan-badge">Cancelling</Badge>
                                : pro ? <Badge className="border border-primary/30 bg-primary/15 text-primary" data-testid="plan-badge">Active</Badge>
                                : <Badge variant="outline" className="border-border text-muted-foreground" data-testid="plan-badge">Free</Badge>}
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground" data-testid="account-plan-detail">
                            {isAdmin && "Admin accounts include Pro permanently."}
                            {!isAdmin && pro && sub.cancel_at_period_end && <>Cancellation scheduled — Pro stays active until <span className="font-semibold text-foreground">{fmtDateFull(sub.current_period_end)}</span>.</>}
                            {!isAdmin && pro && !sub.cancel_at_period_end && <>£4.99/month · renews {sub.current_period_end ? <span className="font-semibold text-foreground">{fmtDateFull(sub.current_period_end)}</span> : "monthly"}.</>}
                            {!pro && "Upgrade to unlock the Savings Finder, subscription tracker, month comparison and unlimited Ask Sift."}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {!isAdmin && pro && !sub.cancel_at_period_end && (
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button variant="outline" disabled={busy} data-testid="cancel-subscription-btn" className="border-destructive/40 bg-transparent text-destructive hover:bg-destructive/10"><XCircle className="mr-1.5 h-4 w-4" /> Cancel subscription</Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-card border-border">
                                    <AlertDialogHeader>
                                        <AlertDialogTitle className="text-foreground">Cancel Sift Pro?</AlertDialogTitle>
                                        <AlertDialogDescription>You'll keep Pro until {fmtDateFull(sub.current_period_end)} and won't be charged again. You can resume any time before then.</AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel data-testid="cancel-subscription-keep-btn" className="border-border bg-secondary text-foreground">Keep Pro</AlertDialogCancel>
                                        <AlertDialogAction onClick={() => act("/payments/cancel", "Subscription will end at period end")} data-testid="cancel-subscription-confirm-btn" className="bg-destructive text-white hover:bg-destructive/90">Yes, cancel</AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        )}
                        {!isAdmin && pro && sub.cancel_at_period_end && (
                            <Button onClick={() => act("/payments/resume", "Welcome back — Pro will keep renewing")} disabled={busy} data-testid="resume-subscription-btn" className="bg-primary text-primary-foreground hover:bg-primary/90">
                                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-1.5 h-4 w-4" />} Resume Pro
                            </Button>
                        )}
                        {!pro && (
                            <Button onClick={() => startCheckout(setBusy)} disabled={busy} data-testid="account-upgrade-btn" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
                                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />} Upgrade to Pro — £4.99/mo
                            </Button>
                        )}
                    </div>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-3">
                    <Stat icon={CalendarClock} label={pro ? (sub.cancel_at_period_end ? "Pro until" : "Next renewal") : "Price"} value={pro ? fmtDateFull(sub.current_period_end) : "£4.99 / month"} testid="account-plan-date" />
                    <Stat icon={Crown} label="Pro features" value={pro ? "Unlocked" : "Locked"} testid="account-plan-features" />
                    <Stat icon={Sparkles} label="Ask Sift" value={pro ? "Unlimited" : "5 questions / day"} testid="account-plan-chat" />
                </div>
            </section>

            <section className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card p-6" data-testid="account-privacy">
                <div>
                    <h3 className="font-heading text-base font-bold text-foreground">Your data</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Statements are stored only to power your insights. Delete everything in one click.</p>
                </div>
                <AlertDialog>
                    <AlertDialogTrigger asChild><Button variant="outline" disabled={busy || !sub.stats.statements} data-testid="delete-data-btn" className="border-destructive/40 bg-transparent text-destructive hover:bg-destructive/10"><Trash2 className="mr-1.5 h-4 w-4" /> Delete all my data</Button></AlertDialogTrigger>
                    <AlertDialogContent className="bg-card border-border">
                        <AlertDialogHeader><AlertDialogTitle className="text-foreground">Delete all statements?</AlertDialogTitle><AlertDialogDescription>This removes every uploaded statement and transaction. Your account stays.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="border-border bg-secondary text-foreground">Keep</AlertDialogCancel>
                            <AlertDialogAction onClick={wipe} data-testid="delete-data-confirm-btn" className="bg-destructive text-white hover:bg-destructive/90">Delete everything</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </section>
            <p className="mt-6 text-xs text-muted-foreground">Payments are processed securely by Stripe. Cancelling stops future charges; you keep access until the period you've paid for ends.</p>
        </AppShell>
    );
}

function Info({ icon: Icon, label, value, testid }) {
    return (
        <div>
            <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3 w-3" /> {label}</p>
            <p className="mt-1 truncate text-sm font-medium text-foreground" data-testid={testid}>{value || "—"}</p>
        </div>
    );
}

function Stat({ icon: Icon, label, value, testid }) {
    return (
        <div className="rounded-xl border border-border bg-background/40 p-4">
            <p className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="h-3 w-3" /> {label}</p>
            <p className="mt-1 font-heading text-base font-bold text-foreground" data-testid={testid}>{value}</p>
        </div>
    );
}
